import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { createUser, type SiteRoleAssignmentInput } from '../../api/users';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import type { CustomerSite } from '../../types/api';

interface NewUserModalProps {
  orgId: string;
  sites: CustomerSite[];
  open: boolean;
  onClose: () => void;
}

interface RoleRow {
  key: number;
  siteId: string;
  role: 'SIGNING_AUTHORITY' | 'END_USER';
}

type OrgRole = 'NONE' | 'CUSTOMER_ADMIN' | 'REPORTS_VIEWER';

let rowKey = 0;

export function NewUserModal({ orgId, sites, open, onClose }: NewUserModalProps) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [orgRole, setOrgRole] = useState<OrgRole>('NONE');
  const [roleRows, setRoleRows] = useState<RoleRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  function addRoleRow() {
    setRoleRows((prev) => [...prev, { key: rowKey++, siteId: sites[0]?.id ?? '', role: 'END_USER' }]);
  }

  function updateRoleRow(key: number, patch: Partial<RoleRow>) {
    setRoleRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function removeRoleRow(key: number) {
    setRoleRows((prev) => prev.filter((r) => r.key !== key));
  }

  function reset() {
    setEmail('');
    setFirstName('');
    setLastName('');
    setPassword('');
    setOrgRole('NONE');
    setRoleRows(sites.length ? [{ key: rowKey++, siteId: sites[0].id, role: 'END_USER' }] : []);
  }

  // Seed one role row by default whenever the modal opens - a user saved
  // with zero roles (no site role, "Customer Admin" left unchecked) is
  // invisible in every list afterwards (findAllForOrg filters by "has a
  // role in this org"), which is exactly what tripped up testing before:
  // it's easy to fill in email/password and hit Create without remembering
  // "Add site role" is a separate step.
  useEffect(() => {
    if (open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => {
      const siteRoles: SiteRoleAssignmentInput[] = roleRows
        .filter((r) => r.siteId)
        .map((r) => ({ siteId: r.siteId, role: r.role }));
      return createUser({
        email,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        password,
        customerAdminForOrgId: orgRole === 'CUSTOMER_ADMIN' ? orgId : undefined,
        reportsViewerForOrgId: orgRole === 'REPORTS_VIEWER' ? orgId : undefined,
        siteRoles: siteRoles.length ? siteRoles : undefined,
      });
    },
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: ['users', orgId] });
      showToast(`${user.email} created.`);
      reset();
      onClose();
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not create the user.')),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const hasSiteRole = roleRows.some((r) => r.siteId);
    if (orgRole === 'NONE' && !hasSiteRole) {
      setError(
        'Assign at least one role (a site role, or an organisation role) so this user can access the organisation.',
      );
      return;
    }
    mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New user"
      size="lg"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button form="new-user-form" type="submit" isLoading={mutation.isPending}>
            Create
          </Button>
        </>
      }
    >
      <form id="new-user-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}
        <Input label="Email" type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
        <div className="grid grid-cols-2 gap-3">
          <Input label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          <Input label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
        <Input
          label="Initial password"
          type="password"
          required
          minLength={8}
          hint="At least 8 characters. The user can be given this to sign in with."
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <Select
          label="Organisation role"
          value={orgRole}
          onChange={(e) => setOrgRole(e.target.value as OrgRole)}
          hint="Customer Admin can manage sites, users, catalog and settings. Reports Viewer can only see the Reports tab."
        >
          <option value="NONE">None</option>
          <option value="CUSTOMER_ADMIN">Customer Admin</option>
          <option value="REPORTS_VIEWER">Reports Viewer</option>
        </Select>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">Site roles</span>
            <Button type="button" variant="ghost" size="sm" onClick={addRoleRow} disabled={sites.length === 0}>
              <Plus className="h-3.5 w-3.5" /> Add site role
            </Button>
          </div>
          {sites.length === 0 ? (
            <p className="text-xs text-slate-500">Create a site first to assign site roles.</p>
          ) : roleRows.length === 0 && orgRole === 'NONE' ? (
            <p className="text-xs text-amber-600">
              Without a site role or an organisation role, this user won't be able to access anything - and won't
              show up in this list.
            </p>
          ) : null}
          <div className="flex flex-col gap-2">
            {roleRows.map((row) => (
              <div key={row.key} className="flex items-center gap-2">
                <Select
                  className="flex-1"
                  value={row.siteId}
                  onChange={(e) => updateRoleRow(row.key, { siteId: e.target.value })}
                >
                  {sites.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
                <Select
                  className="w-44"
                  value={row.role}
                  onChange={(e) => updateRoleRow(row.key, { role: e.target.value as RoleRow['role'] })}
                >
                  <option value="END_USER">End User</option>
                  <option value="SIGNING_AUTHORITY">Signing Authority</option>
                </Select>
                <button
                  type="button"
                  onClick={() => removeRoleRow(row.key)}
                  className="rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </form>
    </Modal>
  );
}
